import React from "react";
import {connect} from "react-redux";
import classNames from "classnames";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import "./FileCount.scss";
import {
    EditorType, ElementsValues, ElementsValuesType, getElementsValues, RefElementsMap,
} from "@data-force/app";
import Nullable from "@data-force/app/interfaces/nullable";
import StoreState from "../../../interfaces/store/storeState";
import {UploadFilesState, UploadsBucket} from "../../../interfaces/store/reducers/uploadFilesState";
import {UploadsData, UploadsHolder} from "../../../interfaces/store/actions/payload/fileValidation";
import {
    batchUploadsAreValid,
    determineNumberOfUploadedFiles,
    getFinishedTasksCount,
    getHistoryParams,
    getUploadFileList,
    initUploadsData,
    isValidNumberOfFiles,
    mergeUploadBuckets,
} from "../../../utils/common";
import HistoryParams from "../../../interfaces/historyParams";

interface StateProps {
    translate: TranslateFunction
    numberUploadFiles: number
    uploadFiles: UploadFilesState
    numberOfTasks: number
    uploadsData: UploadsData
    bucketsCountVisible: boolean
    validBatchUploadsConfig: boolean
}

type Props = StateProps;

class FileCount extends React.Component<Props> {
    private static readonly translationPath: string = "components.FileCount.FileCount";

    constructor(props: Props) {
        super(props);

        this.createBucketCounts = this.createBucketCounts.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        const {
            uploadFiles,
            numberOfTasks,
            bucketsCountVisible,
            numberUploadFiles,
            validBatchUploadsConfig,
            uploadsData: {uploadsHolders},
        }: Props = this.props;
        const {
            uploadFiles: newUploadFiles,
            numberOfTasks: newNumberOfTasks,
            bucketsCountVisible: newBucketsCountVisible,
            numberUploadFiles: newNumberUploadFiles,
            validBatchUploadsConfig: newValidBatchUploadsConfig,
            uploadsData: {uploadsHolders: newUploadHolders},
        }: Props = nextProps;
        return (uploadFiles !== newUploadFiles
            || numberOfTasks !== newNumberOfTasks
            || bucketsCountVisible !== newBucketsCountVisible
            || numberUploadFiles !== newNumberUploadFiles
            || validBatchUploadsConfig !== newValidBatchUploadsConfig
            || uploadsHolders.length !== newUploadHolders.length
        );
    }

    createBucketCounts(): React.ReactNode[] {
        const {
            uploadsData: {uploadsHolders},
            translate,
            uploadFiles,
            validBatchUploadsConfig,
        }: Props = this.props;
        const {translationPath}: typeof FileCount = FileCount;
        const bucketInfos: React.ReactNode[] = [];
        const otherFilesLabel: string = `${translate("components.FilePicker.FilePicker.otherFiles.text") as string}`;
        const otherFilesHolders: UploadsHolder[] = uploadsHolders.filter(
            (holder: UploadsHolder) => holder.name.startsWith(otherFilesLabel) && (holder.minCount || holder.maxCount),
        );
        let otherFilesIndex: number = 1;

        uploadsHolders.forEach((uploadsHolder: UploadsHolder, index: number) => {
            const {name, minCount = 0, maxCount}: UploadsHolder = uploadsHolder;
            if (minCount || maxCount) {
                const uploaded: number = determineNumberOfUploadedFiles({
                    uploadsHolder,
                    uploadFiles,
                    validBatchUploadsConfig,
                });
                let bucketInfoName: string = name;
                if (name.startsWith(otherFilesLabel)) {
                    bucketInfoName = name.substring(0, name.lastIndexOf("_"));
                    if (otherFilesHolders.length > 1) {
                        bucketInfoName = `${bucketInfoName} ${otherFilesIndex}`;
                        otherFilesIndex += 1;
                    }
                }
                const uploadedClasses: string = classNames({
                    "df-bucket-count": true,
                    "df-required-uploads-achieved": isValidNumberOfFiles({
                        minCount, maxCount, uploaded,
                    }),
                });
                let maxMessage: string = `${translate(`${translationPath}.maximum.text`) as string}`;
                if (!maxCount) {
                    maxMessage = `${maxMessage} ${translate(`${translationPath}.undefined.text`) as string}`;
                }

                bucketInfos.push(
                    <div
                        className="df-file-count-bucket"
                        key={`bucket_info_${index}`}
                    >
                        <span className="df-bucket-name">
                            {bucketInfoName}
                        </span>
                        <span className="df-bucket-count">
                            <span className="screen-reader-only">
                                <Translate id={`${translationPath}.minimum.text`}/>
                            </span>
                            {minCount}
                        </span>
                        <span className="df-bucket-count">
                            <span className="screen-reader-only">
                                {maxMessage}
                            </span>
                            <span aria-hidden={!maxCount}>
                                {maxCount || "-"}
                            </span>
                        </span>
                        <span className={uploadedClasses}>
                            <span className="screen-reader-only">
                                <Translate id={`${translationPath}.added.text`}/>
                            </span>
                            {uploaded}
                        </span>
                    </div>,
                );
            }
        });
        return bucketInfos;
    }

    createOverallCount(): React.ReactNode {
        const {numberUploadFiles, numberOfTasks, translate}: Props = this.props;
        const {translationPath}: typeof FileCount = FileCount;
        const outOff: string = ` ${translate(`${translationPath}.outOff.text`) as string} `;

        const uploadedVsTotalClassName: string = classNames({
            "df-required-uploads-achieved": numberUploadFiles === numberOfTasks,
        });
        return (
            <div
                className="df-file-count-accent"
                key="overall-file-count-key"
            >
                <span className="df-bucket-name">
                    <Translate id={`${translationPath}.totalNumberOfFiles.text`}/>
                </span>
                <span className="df-bucket-count"/>
                <span className="df-bucket-count"/>
                <span className="df-bucket-count">
                    {numberUploadFiles}
                    <span aria-hidden="true">/</span>
                    <span className="screen-reader-only">{outOff}</span>
                    <span className={uploadedVsTotalClassName}>{numberOfTasks}</span>
                </span>
            </div>
        );
    }

    render() {
        const {translationPath}: typeof FileCount = FileCount;
        const {bucketsCountVisible}: Props = this.props;

        return (
            <div className="df-file-count">
                {bucketsCountVisible
                && (
                    <div
                        className="df-file-count-accent"
                        key="file-count-header-key"
                    >
                        <h2 className="df-bucket-name df-bucket-name-label">
                            <Translate id={`${translationPath}.uploadFiles.text`}/>
                        </h2>
                        <span className="df-bucket-count">
                            <span className="screen-reader-only">
                                <Translate id={`${translationPath}.minimum.text`}/>
                            </span>
                            <Translate aria-hidden="true" id={`${translationPath}.min.text`}/>
                        </span>
                        <span className="df-bucket-count">
                            <span className="screen-reader-only">
                                <Translate id={`${translationPath}.maximum.text`}/>
                            </span>
                            <Translate aria-hidden="true" id={`${translationPath}.max.text`}/>
                        </span>
                        <span className="df-bucket-count">
                            <Translate id={`${translationPath}.added.text`}/>
                        </span>
                    </div>
                )}
                {this.createBucketCounts()}
                {this.createOverallCount()}
            </div>
        );
    }
}

function mapStateToProps(
    {
        localize,
        uploadFiles,
        job,
        schema: {parsedSchemas, elementsValuesMap},
        project,
    }: StoreState,
): StateProps {
    const {selectedFiles, finishedUploads: {bucketCount}}: UploadFilesState = uploadFiles;
    const numberUploadFiles: number = getUploadFileList(selectedFiles).length + getFinishedTasksCount(bucketCount);
    const numberOfTasks: number = job?.tasks.length || 0;
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: EditorType = parsedSchemas[schemaKey] as EditorType;
    const collectionRefElements: RefElementsMap = editor?.refElements || {};
    const elementsValues: ElementsValues = getElementsValues({
        metadataId: historyParams?.jobId,
        elementsValuesType: ElementsValuesType.JOB,
        schemaKey,
        elementsValuesMap,
    });
    const uploadBucket: UploadsBucket = mergeUploadBuckets(selectedFiles);
    const translate: TranslateFunction = getTranslate(localize);
    const uploadsData: UploadsData = initUploadsData({
        editor,
        collectionRefElements,
        elementsValues,
        translate,
        translationPath: "components.FilePicker.FilePicker",
        tasks: job?.tasks || [],
        elementsValuesMap,
        schemaKey,
        uploadBucket,
    });

    const bucketsCountVisible: boolean = uploadsData.uploadsHolders.findIndex(
        (holder: UploadsHolder): boolean => (holder.minCount !== undefined || holder.maxCount !== undefined),
    ) >= 0;

    const validBatchUploadsConfig: boolean = editor?.batchUploads && editor?.uploadReference
        ? batchUploadsAreValid(editor.batchUploads, editor.uploadReference)
        : false;

    return {
        translate,
        uploadFiles,
        numberUploadFiles,
        numberOfTasks,
        uploadsData,
        bucketsCountVisible,
        validBatchUploadsConfig,
    };
}

export default connect(mapStateToProps)(FileCount);
